import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";
import dayjs from "dayjs";

export class ValidatesJP {
  /**---------------------------------------------
   * ValidatesJPクラス
   * @description
   * 日本の電話番号、メールアドレス、パスワードの検証およびフォーマットを行うクラス。
   * コンストラクタで設定を受け取り、指定がなければデフォルト値を使用します。
   * @param {object} [config] - オプションの設定オブジェクト
   * @param {number} [config.passwordMinLength=8] - パスワードの最小文字数
   * @param {number} [config.passwordMaxLength=16] - パスワードの最大文字数
   * @param {string} [config.formatWidth='half'] - String, Phoneメソッドのフォーマット幅
   *
   * @method String - 文字列のフォーマット変換
   * @method Phone - 電話番号の検証
   * @method Email - メールアドレスの検証
   * @method Password - パスワードの検証
   *
   * @example
   * const validator = new ValidatesJP({ passwordMinLength: 6, passwordMaxLength: 12 });
   * const phoneResult = validator.Phone('090-1234-5678');
   * console.log(phoneResult); // { method: "Phone", message: "", input: "090-1234-5678", output: "090-1234-5678", isValid: true }
   **-----------------------------------------------*/
  constructor(config = {}) {
    // コンストラクタで受け取った設定をメンバ変数に設定、指定がなければデフォルト値を使用
    this.passwordMinLength = config.passwordMinLength || 8;
    this.passwordMaxLength = config.passwordMaxLength || 16;
    this.loginNoMinLength = config.loginNoMinLength || 5;
    this.loginNoMaxLength = config.loginNoMaxLength || 6;
    this.formatWidth = config.formatWidth || `half`;
  }

  checkAndReturnEmpty(str) {
    // strが文字列であるかを確認
    if (typeof str !== "string") {
      return "";
    }
    // 文字列から半角スペースと全角スペースを全て除去
    const trimmed = str.replace(/[\s　]/g, "");
    // 除去後に文字列が空であれば、全てスペースのみ
    return trimmed === "" ? "" : str;
  }
  /**---------------------------------------------
   * 全角文字を半角に変換
   *
   * @param {string} str - 変換対象の文字列
   * @return {string} - 半角に変換された文字列
   **-----------------------------------------------*/
  toHalfWidth(str) {
    str = String(str);
    return str
      .replace(/[！-～]/g, (char) => {
        return String.fromCharCode(char.charCodeAt(0) - 0xfee0);
      })
      .replace(/　/g, " "); // 全角スペースを半角スペースに変換
  }

  /**---------------------------------------------
   * 半角文字を全角に変換
   *
   * @param {string} str - 変換対象の文字列
   * @return {string} - 全角に変換された文字列
   **-----------------------------------------------*/
  toFullWidth(str) {
    return str
      .replace(/[!-~]/g, (char) => {
        return String.fromCharCode(char.charCodeAt(0) + 0xfee0);
      })
      .replace(/ /g, "　"); // 半角スペースを全角スペースに変換
  }
  /**---------------------------------------------
   * 文字列を半角または全角に変換
   **-----------------------------------------------*/
  String(
    string,
    { width = this.formatWidth, title = "文字列", required = false } = {}
  ) {
    const result = {
      method: "String",
      message: `${width === `half` ? `半角` : `全角`}に変換しました`,
      input: string,
      output: string,
      isValid: null,
    };



  // 配列かどうかを判定
  const isArray = Array.isArray(string);

  // 空チェック（配列なら全要素が空白または空文字か）
  const isEmpty =
    (isArray &&
      string.every((s) => this.checkAndReturnEmpty(s) === ``)) ||
    (!isArray && this.checkAndReturnEmpty(string) === ``);

  if (isEmpty) {
    result["message"] = `${title}が空です`;
    result["output"] = isArray ? string.map(() => ``) : ``;

    if (required) {
      result["message"] = `「${title}」は必須項目です`;
      result["isValid"] = false;
    }

    return result;
  }

    result["isValid"] = true;

    if (width === `half`) {
      result["output"] = isArray
        ? string.map((s) => this.toHalfWidth(s))
        : this.toHalfWidth(string);
      return result;
    }

    if (width === `full`) {
      result["output"] = isArray
        ? string.map((s) => this.toFullWidth(s))
        : this.toFullWidth(string);
      return result;
    }
  }


  #isAllHiragana(str,removeSpace=true) {
    if(!removeSpace){
      // 正規表現で「全角ひらがな」のみかをチェック
      return /^[\u3040-\u309Fー\s\u3000]*$/.test(str);
    }
    return /^[\u3040-\u309Fー]*$/.test(str);


  }



  #isValidKanaString(str) {
    // 正規表現で全角ひらがな、全角カタカナ、半角カタカナ以外をチェック
    return /^[\u3040-\u309F\u30A0-\u30FF\uFF61-\uFF9F]*$/.test(str);
  }
  #katakanaToHiragana(str) {
    // 半角カタカナを全角カタカナに変換
    const fullWidthKatakana = str.replace(/[\uFF61-\uFF9F]/g, (char) => {
      const conversionTable = {
        ｱ: "ア",
        ｲ: "イ",
        ｳ: "ウ",
        ｴ: "エ",
        ｵ: "オ",
        ｶ: "カ",
        ｷ: "キ",
        ｸ: "ク",
        ｹ: "ケ",
        ｺ: "コ",
        ｻ: "サ",
        ｼ: "シ",
        ｽ: "ス",
        ｾ: "セ",
        ｿ: "ソ",
        ﾀ: "タ",
        ﾁ: "チ",
        ﾂ: "ツ",
        ﾃ: "テ",
        ﾄ: "ト",
        ﾅ: "ナ",
        ﾆ: "ニ",
        ﾇ: "ヌ",
        ﾈ: "ネ",
        ﾉ: "ノ",
        ﾊ: "ハ",
        ﾋ: "ヒ",
        ﾌ: "フ",
        ﾍ: "ヘ",
        ﾎ: "ホ",
        ﾏ: "マ",
        ﾐ: "ミ",
        ﾑ: "ム",
        ﾒ: "メ",
        ﾓ: "モ",
        ﾔ: "ヤ",
        ﾕ: "ユ",
        ﾖ: "ヨ",
        ﾗ: "ラ",
        ﾘ: "リ",
        ﾙ: "ル",
        ﾚ: "レ",
        ﾛ: "ロ",
        ﾜ: "ワ",
        ﾝ: "ン",
        ｧ: "ァ",
        ｨ: "ィ",
        ｩ: "ゥ",
        ｪ: "ェ",
        ｫ: "ォ",
        ｯ: "ッ",
        ｬ: "ャ",
        ｭ: "ュ",
        ｮ: "ョ",
        // 他の半角カタカナも必要に応じて追加
      };
      return (
        conversionTable[char] || String.fromCharCode(char.charCodeAt(0) + 0x60)
      );
    });

    // 全角カタカナをひらがなに変換
    const hiragana = fullWidthKatakana.replace(
      /[\u30A1-\u30FA\u30FC]/g,
      (char) => String.fromCharCode(char.charCodeAt(0) - 0x60)
    );

    return hiragana;
  }

  /**---------------------------------------------
   * 文字列に対してのひらがなチェック
   * ひらがな以外があれば、ひらがなに変換する
   * ひらがなに変換できない文字が入っていた場合はエラー
   * @param {string} string - 文字列
   * @return {object} - 検証結果オブジェクト
   * @property {string} method - メソッド名 ("Hiragana")
   * @property {string} message - エラーメッセージ
   * @property {string} input - 入力された文字列
   * @property {string} output - フォーマット済みの文字列
   * @property {boolean} isValid - 有効な文字列かどうか
   **-----------------------------------------------*/
  Hiragana(
    string,
    { removeSpace = true, title = "文字列", required = false } = {}
  ) {
    const result = {
      method: "Hiragana",
      message: ``,
      input: string,
      output: string,
      isValid: null,
    };

    const isArray = Array.isArray(string);

    // スペース除去後の配列 or 単体文字列に変換
    const cleaned = isArray
      ? string.map((s) => removeSpace ? s.replace(/[\s　]/g, "") : s)
      : removeSpace
        ? string.replace(/[\s　]/g, "")
        : string;

    // 空チェック（全て空なら isValid=false）
    const isEmpty = isArray
      ? cleaned.every((s) => this.checkAndReturnEmpty(s) === ``)
      : this.checkAndReturnEmpty(cleaned) === ``;

    if (isEmpty) {
      result["message"] = `${title}が空です`;
      result["output"] = isArray ? string.map(() => ``) : ``;
      if (required) {
        result["message"] = `「${title}」は必須項目です`;
        result["isValid"] = false;
      }
      return result;
    }

    // 妥当性チェック（1つでもひらがな以外があれば不正）
    const allValid = isArray
      ? cleaned.every((s) => this.#isAllHiragana(s, removeSpace))
      : this.#isAllHiragana(cleaned, removeSpace);

    if (!allValid) {
      result["isValid"] = false;
      result["message"] = removeSpace === false
        ? `${title}は、全角ひらがなとスペースのみで入力してください`
        : `${title}は、全角ひらがなのみで入力してください`;
      result["output"] = string;
      return result;
    }

    result["isValid"] = true;
    result["output"] = string;
    return result;
  }


  /**---------------------------------------------
   * 日本の電話番号を解析・フォーマット
   *
   * @param {string} phoneNumber - 電話番号文字列
   * @return {object} - 検証結果オブジェクト
   * @property {string} method - メソッド名 ("Phone")
   * @property {string} message - エラーメッセージ
   * @property {string} input - 入力された電話番号
   * @property {string} output - フォーマット済みの電話番号
   * @property {boolean} isValid - 有効な電話番号かどうか
   **-----------------------------------------------*/
  Phone(phoneNumber, { title = "電話番号", required = false } = {}) {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const result = {
      method: "Phone",
      message: "",
      input: phoneNumber,
      output: phoneNumber,
      isValid: null,
    };

    if (this.checkAndReturnEmpty(phoneNumber) === ``) {
      result["message"] = `${title}が空欄になっています`;
      result["output"] = ``;
      if (required) {
        result["message"] = `「${title}」は必須項目です`;
        result["isValid"] = false;
      }

      return result;
    }

    const regex = /^[0０]/;
    if (!regex.test(phoneNumber)) {
      result["message"] = `最初の文字は0で始めてください`;
      result["isValid"] = false;
      return result;
    }

    try {
      const number = phoneUtil.parseAndKeepRawInput(phoneNumber, "JP");
      const isValid = phoneUtil.isValidNumber(number);
      result["isValid"] = isValid;

      if (!isValid) {
        result["message"] = `無効な${title}です`;
        return result;
      }
      const nationalFormat = phoneUtil.format(
        number,
        PhoneNumberFormat.NATIONAL
      );
      //result["NationalFormat"] = nationalFormat;
      result["output"] =
        this.formatWidth === `half`
          ? nationalFormat
          : this.toFullWidth(nationalFormat);
      return result;
    } catch (error) {
      result["message"] = `適切な${title}フォーマットではありません`;
      result["isValid"] = false;
      return result;
    }
  }

  /**---------------------------------------------
   * メールアドレスのフォーマットチェック
   *
   * @param {string} mailAddress - メールアドレス文字列
   * @return {object} - 検証結果オブジェクト
   * @property {string} method - メソッド名 ("Email")
   * @property {string} message - エラーメッセージ
   * @property {string} input - 入力されたメールアドレス
   * @property {string} output - フォーマット済みのメールアドレス
   * @property {boolean} isValid - 有効なメールアドレスかどうか
   **-----------------------------------------------*/
  Email(mailAddress, { title = "メールアドレス", required = false } = {}) {
    const result = {
      method: "Email",
      message: "",
      input: mailAddress,
      output: mailAddress,
      isValid: null,
    };

    if (this.checkAndReturnEmpty(mailAddress) === ``) {
      result["message"] = `${title}が空欄になっています`;
      result["output"] = ``;
      if (required) {
        result["message"] = `「${title}」は必須項目です`;
        result["isValid"] = false;
      }

      return result;
    }

    const formatMailAddress = this.toHalfWidth(mailAddress);
    // const regex = /^[a-z][a-z0-9_-]*(?:\.[a-z0-9_-]+)*@[a-z0-9-]+(?:\.[a-z0-9-]+)*$/i;
    const regex =
      /^[a-z][a-z0-9_-]*(?:\.[a-z0-9_-]+)*@[a-z0-9-]+\.[a-z0-9-]+(?:\.[a-z0-9-]+)*$/i;

    result["isValid"] = regex.test(formatMailAddress);

    if (!result["isValid"]) {
      if (!/^[a-z]/i.test(formatMailAddress)) {
        result["message"] = `最初の文字はアルファベットで始めてください`;
      } else if (/[^a-z0-9._\-@]/i.test(formatMailAddress)) {
        result[
          "message"
        ] = `アルファベット、数字、記号「 - . _ 」のみ許可されています`;
      } else if (/(\.{2,})/.test(formatMailAddress)) {
        result["message"] = `2連続以上のドットは許可されていません`;
      } else if (/\.@/.test(formatMailAddress)) {
        result["message"] = `@直前のドットは許可されていません`;
      } else if (!/@/.test(formatMailAddress)) {
        result["message"] = `@が含まれていません`;
      } else {
        result["message"] = `適切な${title}フォーマットではありません`;
      }
    }
    result["output"] = formatMailAddress;
    return result;
  }

  /**---------------------------------------------
   * パスワードのフォーマットチェック
   *
   * @param {string} password - パスワード文字列
   * @return {object} - 検証結果オブジェクト
   * @property {string} method - メソッド名 ("Password")
   * @property {string} message - エラーメッセージ
   * @property {string} input - 入力されたパスワード
   * @property {string} output - フォーマット済みのパスワード
   * @property {boolean} isValid - 有効なパスワードかどうか
   **-----------------------------------------------*/
  Password(password, { title = "パスワード", required = false } = {}) {
    const result = {
      method: "Password",
      message: "",
      input: password,
      output: password,
      isValid: null,
    };

    if (this.checkAndReturnEmpty(password) === ``) {
      result["message"] = `${title}が空欄になっています`;
      result["output"] = ``;
      if (required) {
        result["message"] = `「${title}」は必須項目です`;
        result["isValid"] = false;
      }

      return result;
    }

    const formatPassword = this.toHalfWidth(password);
    const regex = new RegExp(
      `^[a-zA-Z0-9@._*$%/&+-]{${this.passwordMinLength},${this.passwordMaxLength}}$`
    );
    result["isValid"] = regex.test(formatPassword);

    if (!result["isValid"]) {
      if (
        password.length < this.passwordMinLength ||
        password.length > this.passwordMaxLength
      ) {
        result[
          "message"
        ] = `${this.passwordMinLength}～${this.passwordMaxLength}文字でお願いいたします。`;
      } else if (!/^[a-zA-Z0-9@._*$%/&+-]+$/.test(password)) {
        result[
          "message"
        ] = `半角英数字、記号「@._*$%/&+-」のみが許可されています`;
      } else {
        result["message"] = `適切な${title}フォーマットではありません`;
      }
      return result;
    }

    result["output"] = formatPassword;
    return result;
  }

  /**---------------------------------------------
   * 会員番号のフォーマットチェック
   *
   * @param {string} loginNo - 会員番号文字列
   * @return {object} - 検証結果オブジェクト
   * @property {string} method - メソッド名 ("LoginNo")
   * @property {string} message - エラーメッセージ
   * @property {string} input - 入力された会員番号
   * @property {string} output - フォーマット済みの会員番号
   * @property {boolean} isValid - 有効な会員番号かどうか
   **-----------------------------------------------*/
  LoginNo(loginNo, { title = "会員番号", required = false } = {}) {
    const result = {
      method: "LoginNo",
      message: "",
      input: loginNo,
      output: loginNo,
      isValid: null,
    };

    if (this.checkAndReturnEmpty(loginNo) === ``) {
      result["message"] = `${title}が空欄になっています`;
      result["output"] = ``;
      if (required) {
        result["message"] = `「${title}」は必須項目です`;
        result["isValid"] = false;
      }
      return result;
    }

    const formatLoginNo = this.toHalfWidth(loginNo);
    //const regex = new RegExp(`^[0-9]{${this.loginNoMinLength},${this.loginNoMaxLength}}$`);
    const regex = new RegExp(
      `^(?!0+$)[0-9]{${this.loginNoMinLength},${this.loginNoMaxLength}}$`
    );
    result["isValid"] = regex.test(formatLoginNo);
    if (!result["isValid"]) {
      //formatLoginNoが数字のみで構成されていない場合
      if (!/^[0-9]+$/.test(formatLoginNo)) {
        result[
          "message"
        ] = `会員番号は数字のみで${this.loginNoMinLength}～${this.loginNoMaxLength}文字となっています`;
      }
      //formatLoginNoが0のみで構成されている場合
      else if (/^0+$/.test(formatLoginNo)) {
        result["message"] = `無効な会員番号です`;
      } else {
        result[
          "message"
        ] = `会員番号は数字のみで${this.loginNoMinLength}～${this.loginNoMaxLength}文字となっています`;
      }

      return result;
    }

    result["output"] = formatLoginNo;
    return result;
  }

  /**---------------------------------------------
   * 日付のフォーマットチェック
   *
   * @param {string} loginNo - 日付文字列
   * @return {object} - 検証結果オブジェクト
   * @property {string} method - メソッド名
   * @property {string} message - エラーメッセージ
   * @property {string} input - 入力された日付
   * @property {string} output - フォーマット済みの日付
   * @property {boolean} isValid - 有効な日付かどうか
   **-----------------------------------------------*/
  #convertToWareki(dateString) {
    const dt = Date.parse(`${dateString} 00:00:00`);
    const formattedDate = new Intl.DateTimeFormat("ja-JP-u-ca-japanese", {
      era: "long",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(dt);
    const regex = /^([^\d]+)(\d+)\/(\d+)\/(\d+)$/;
    const match = formattedDate.match(regex);
    if (match) {
      let [_, era, year, month, day] = match;
      if (era === "明治" || era === "大正") {
        year = parseInt(year, 10);
      }

      return [
        era,
        String(Number(year)),
        String(Number(month)),
        String(Number(day)),
      ];
    }
    return null;
  }
  #formatDateCustom(dateString) {
    //数値以外の文字列を削除
    dateString = dateString.replace(/\D/g, "");
    const year = parseInt(dateString.slice(0, 4), 10);
    const month = parseInt(dateString.slice(4, 6), 10);
    const day = parseInt(dateString.slice(6, 8), 10);
    // 月や日の範囲をチェックしない（そのまま受け取る）
    return `${year.toString().padStart(4, "0")}-${month
      .toString()
      .padStart(2, "0")}-${day.toString().padStart(2, "0")}`;
  }
  // うるう年判定関数
  #isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  }
  #formatWareki = (warekiArray) => {
    if (!Array.isArray(warekiArray)) {
      console.log(warekiArray);
      return `未入力`;
    }
    const [era, year, month, day] = warekiArray;
    if (era === "昭和" && year === "" && month === "" && day === "") {
      return `未入力`;
    }
    // Convert year, month, and day to integers to remove leading zeros
    const formattedYear = parseInt(year, 10);
    const formattedMonth = parseInt(month, 10);
    const formattedDay = parseInt(day, 10);
    // Return the formatted string

    // formattedYearが1の場合は、「元」と表示
    if (formattedYear === 1) {
      return `${era}元年${formattedMonth}月${formattedDay}日`;
    }
    return `${era}${formattedYear}年${formattedMonth}月${formattedDay}日`;
  };

  Date(dateString, { title = "日付", required = false } = {}) {
    const result = {
      method: "Date",
      message: "",
      input: dateString,
      output: dateString,
      output_wareki_object: ["昭和", "", "", ""],
      output_wareki: "",
      output_format: "",
      isValid: null,
    };

    // 1. 空文字、"0000-00-00"、"00000000" の場合
    if (
      dateString === `` ||
      dateString === `0000-00-00` ||
      dateString === `00000000`
    ) {
      result["message"] = `日付が空です`;
      result["isValid"] = null;
      if (required) {
        result["message"] = `「${title}」は必須項目です`;
        result["isValid"] = false;
      }
      return result;
    }

    // 2. dayjs で有効な日付かどうかをチェック

    const formatDateString = this.toHalfWidth(dateString);

    const date = dayjs(formatDateString);
    console.log(date);

    if (!date.isValid()) {
      result["message"] = `存在しない日付です`;
      result["isValid"] = false;
      return result;
    }

    // 3. 正しい日付形式かをチェック
    // const datePattern = /^(?!0000-00-00)\d{4}-\d{2}-\d{2}$/;
    // 入力された文字列でYYYY-MM-DDにフォーマット
    const dateStringTemp = this.#formatDateCustom(formatDateString);
    const datePattern =
      /^(?!0000)\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
    if (!datePattern.test(dateStringTemp)) {
      result["message"] = `不正な日付フォーマットです`;
      result["isValid"] = false;
      return result;
    }

    // 4. うるう年を考慮した2月の日付チェック
    const [year, month, day] = dateStringTemp.split("-").map(Number);
    if (month === 2 && day > 29) {
      result["message"] = `存在しない日付です`;
      result["isValid"] = false;
      return result;
    }
    if (month === 2 && day === 29 && !this.#isLeapYear(year)) {
      result["message"] = `うるう年ではないため、存在しない日付です`;
      result["isValid"] = false;
      return result;
    }

    // 有効な日付の場合
    result["isValid"] = true;
    result["output"] = date.format("YYYY-MM-DD");
    result["output_format"] = date.format("YYYY年M月D日");

    result["output_wareki_object"] = this.#convertToWareki(result["output"]);
    result["output_wareki"] = this.#formatWareki(
      result["output_wareki_object"]
    );
    return result;
  }

  Wareki([era, year, month, day], { title = "日付", required = false } = {}) {
    const result = {
      method: "DateWareki",
      message: "",
      input: [era, year, month, day],
      output: "0000-00-00",
      isValid: null,
    };

    if (year === `` || month === `` || day === ``) {
      result["message"] = `日付が空です`;
      result["isValid"] = null;
      if (required) {
        result["message"] = `「${title}」は必須項目です`;
        result["isValid"] = false;
      }
      return result;
    }

    // 和暦の元号に対応する開始日と終了日を定義
    const eraMapping = {
      明治: { start: "1868-10-23", end: "1912-07-29" },
      大正: { start: "1912-07-30", end: "1926-12-24" },
      昭和: { start: "1926-12-25", end: "1989-01-07" },
      平成: { start: "1989-01-08", end: "2019-04-30" },
      令和: {
        start: "2019-05-01",
        end: `${new Date().getFullYear()}-${(new Date().getMonth() + 1)
          .toString()
          .padStart(2, "0")}-${new Date()
          .getDate()
          .toString()
          .padStart(2, "0")}`,
      }, // 現在の日付まで
    };

    // 1.元号が有効かを確認
    if (!eraMapping[era]) {
      result["message"] = `無効な元号です: ${era}`;
      result["isValid"] = false;
      return result;
    }

    // 2. 和暦年・月・日の西暦変換
    const formatYear = this.toHalfWidth(year);
    const formatMonth = this.toHalfWidth(month);
    const formatDay = this.toHalfWidth(day);

    const yearNumber = parseInt(formatYear, 10);
    const seirekiYear =
      parseInt(eraMapping[era].start.split("-")[0], 10) + yearNumber - 1;
    const dateString = `${seirekiYear}-${formatMonth.padStart(
      2,
      "0"
    )}-${formatDay.padStart(2, "0")}`;

    // 3. 日付の妥当性をチェック
    const startDate = new Date(eraMapping[era].start);
    const endDate = new Date(eraMapping[era].end);
    const targetDate = new Date(dateString);
    if (
      (isNaN(targetDate.getTime()) ||
        targetDate < startDate ||
        targetDate > endDate) &&
      era !== "令和"
    ) {
      result.message = `無効な日付です（元号「${era}」の範囲外）`;
      result.isValid = false;
      return result;
    }

    result["output"] = dateString;
    result["output_wareki"] = this.#formatWareki(result["input"]);
    result["isValid"] = true;
    return result;
  }

  /**---------------------------------------------
   * 日付の前後関係チェック
   *
   * @param {array} dates - 日付文字列
   * @return {object} - 検証結果オブジェクト
   * @property {string} method - メソッド名
   * @property {string} message - エラーメッセージ
   * @property {array} input - 入力された日付
   * @property {array} output - フォーマット済みの日付
   * @property {boolean} isValid - 前後関係が合ってるかどうか
   **-----------------------------------------------*/

  DateOrder(dates = []) {
    console.log(dates);
    const startDate = dates[0];
    const endDate = dates[1];
    const result = {
      method: "DateOrder",
      message: "",
      input: [startDate, endDate],
      output: [startDate, endDate],
      isValid: null,
    };

    if (!this.Date(startDate).isValid || !this.Date(endDate).isValid) {
      if (!this.Date(startDate).isValid) {
        result["message"] = `判定不能 開始日付のフォーマットが正しくありません`;
      }
      if (!this.Date(endDate).isValid) {
        result["message"] = `判定不能 終了日付のフォーマットが正しくありません`;
      }

      result["isValid"] = null;
      return result;
    }
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    if (start.isAfter(end)) {
      result["message"] = `開始日が終了日より後になっています`;
      result["isValid"] = false;
      result["output"] = [endDate, startDate];
      return result;
    }
    result["isValid"] = true;
    return result;
  }
}

export const Validation = {
  validator:null,
  results:{},
  options:{},
  form:null,
  init(schemas,ops={}){
    console.log(`Validation init`);
    // オプションの初期値を設定してopsで上書き&this.optionsに格納
    this.options = {
      targetFormId: `form`, // バリデーションを実行するフォームのID
      trigger: 'both', // 'blur' | 'submit' | 'both'
      backgroundClass: 'has-background-warning-light',
      errorClass: 'validate-error',
      resultBoxId: 'result',
      submitButtonClass: 'btnSubmit',
      preventSubmit: true,
      outputFormated: true, // フォーマットを適用するかどうか
      ...ops
    };
    this.form = document.getElementById(this.options.targetFormId);
    if(!this.form){
      console.warn(`Validation init warning: 指定されたフォームが見つかりません。`);
      return;
    }

    this.validator = new ValidatesJP({
      passwordMinLength: 8,
      passwordMaxLength: 16,
      formatWidth: `half`,
    });

// 必須タグの追加
schemas.forEach((v) => {
  if (v.required) {
    const names = Array.isArray(v.name) ? v.name : [v.name]; // ← 配列に変換

    names.forEach((name) => {
      const input = this.form.querySelector(`[name="${name}"]`);
      if (!input) return;

      const parent = input.closest(`.field.is-horizontal`);
      if (!parent) return;

      const label = parent.querySelector(`.label`);
      if (!label || label.querySelector('.tag.is-primary')) return; // ← 重複防止（任意）

      label.insertAdjacentHTML(
        'beforeend',
        `<span class="tag has-background-black has-text-white">必須</span>`
      );
    });
  }
});


  // blurの処理
  schemas.forEach((v) => {
    const names = Array.isArray(v.name) ? v.name : [v.name]; // ← 配列に変換
    names.forEach((name) => {
      const inputs = this.form.querySelectorAll(`[name="${name}"]`);
      inputs.forEach((input) => {
        if (this.options.trigger === 'blur' || this.options.trigger === 'both') {
          input.addEventListener('blur', () => {
            this.runValidation(input, v);
            this.handleResults(this.options);
          });
        }
      });
    });
  });
    // submit イベント登録
    if (this.options.trigger === 'submit' || this.options.trigger === 'both') {
      const buttons = this.form.querySelectorAll(`.${this.options.submitButtonClass}`);
      buttons.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          if (this.options.preventSubmit) e.preventDefault();
          const isValidAll = this.runValidationAll(schemas);
          if(isValidAll){
            this.form.submit(); // フォームを送信する
          }
        });
      });
    }
    // ✅ 初期化直後に一度バリデーション（必要なら）
    //this.runValidationAll(schemas);
  },
  runValidation(input, v) {
    const result = this.validator[v.method](input.value, v);
    if (this.options.outputFormated) {
      input.value = result.output;
    }
    this.results[v.name] = result;
  },
  runValidationAll(schemas) {
    let isValidAll = true;
    schemas.forEach((v) => {
      const input = this.form.querySelector(`[name="${v.name}"]`);
      if (input) {
        this.runValidation(input, v);
        if (this.results[v.name].isValid === false) {
          isValidAll = false;
        }
      }
    });
    this.handleResults(this.options);
    return isValidAll;
  },
  handleResults({
    backgroundClass = `has-background-warning-light`,
    errorClass = `validate-error`
  } = {}) {
    Object.keys(this.results).forEach((key) => {
      const result = this.results[key];

      // カンマ区切りなら分割、それ以外は単一として扱う
      const names = key.includes(",") ? key.split(",") : [key];

      names.forEach((name, idx) => {
        const elements = this.form.querySelectorAll(`[name="${name}"]`);

        elements.forEach((element, k) => {
          const parent = element.parentNode;

          // 背景クラス除去
          element.classList.remove(backgroundClass);

          // エラーメッセージ削除
          parent.querySelectorAll(`.${errorClass}`).forEach((el) => el.remove());

          // エラー処理
          if (result.isValid === false) {
            element.classList.add(backgroundClass);

            // 最初の1つの要素だけにメッセージ表示（重複防止）
            if (idx === 0 && k === 0) {
              parent.insertAdjacentHTML(
                'beforeend',
                `<p class="${errorClass} has-text-danger">${result.message}</p>`
              );
            }
          }
        });
      });
    });

    // isValid=false の項目のみ抽出
    const errors = Object.entries(this.results).filter(
      ([, res]) => res.isValid === false
    );

    console.log(`errors`, errors.map(([k]) => k));

    // submitボタン制御
    if (this.options.preventSubmit) {
      this.form.querySelector(`.${this.options.submitButtonClass}`).disabled = false;
    }

    const resultArea = this.form.querySelector(`#${this.options.resultBoxId}`);
    resultArea.innerHTML = ``;

    if (errors.length > 0) {
      if (this.options.preventSubmit) {
        this.form.querySelector(`.${this.options.submitButtonClass}`).disabled = true;
      }
      resultArea.innerHTML = this.insertErrorBox(errors.map(([k]) => k));
    }
  },

  insertErrorBox(errors){
    return `<div class="notification is-danger is-light my-6">
    <p>入力内容をご確認ください</p>
    <ul class="p-2">
    ${errors.map(v=>{
      return `<li>${this.results[v].message}</li>`
    }).join("")}
    </ul>
    </div>`;
  }
}
